import { clsx } from 'clsx';
import { forwardRef, useId, useImperativeHandle, useState } from 'react';

import type { KeyboardEvent, ReactNode } from 'react';

import styles from './Accordion.module.css';

import { Icon } from 'components/Icon';

export interface AccordionProps {
  /** Clickable title (always visible). */
  title: ReactNode;
  /** Main content (hidden when expanded is false). */
  content: ReactNode;
  /** Indicator if Accordion should be expanded by default.*/
  expanded?: boolean;
  /** Indicator for cheveron icon placement.*/
  cheveronPlacement?: 'left' | 'right';
  /** Indicator for cheveron icon margin. By default '050'. */
  cheveronMargin?: '0' | '025' | '050' | '075' | '100' | '150' | '200';
}

export interface AccordionMethods {
  closeContent: () => void;
  openContent: () => void;
}

/** A controller for accordion display toggling of content with click on a title. */
export const Accordion = forwardRef<AccordionMethods, AccordionProps>(
  ({ title, content, expanded = false, cheveronPlacement = 'right', cheveronMargin = '050' }, ref) => {
    const [isExpanded, setIsExpanded] = useState(expanded);
    const accordionId = useId();

    function onKeyUp(event: KeyboardEvent<HTMLDivElement>) {
      const { code } = event;

      if (code === 'Enter' || code === 'Space') {
        setIsExpanded(!isExpanded);
      }
    }

    const closeContent = () => {
      setIsExpanded(false);
    };

    const openContent = () => {
      setIsExpanded(true);
    };

    useImperativeHandle(ref, () => ({
      closeContent,
      openContent,
    }));

    return (
      <div>
        <div className={styles['accordion']}>
          <div
            className={styles['accordion__title']}
            id={accordionId}
            role="button"
            tabIndex={0}
            onClick={() => setIsExpanded(!isExpanded)}
            onKeyUp={onKeyUp}
            aria-expanded={isExpanded}
          >
            {cheveronPlacement === 'left' && (
              <Icon
                className={clsx(
                  styles['accordion__title-icon'],
                  styles[`accordion__title-icon--margin-${cheveronMargin}`],
                  {
                    [styles['accordion__title-icon--expanded']]: isExpanded,
                  }
                )}
                name="CheveronLeft"
                size={16}
              />
            )}

            {title}
            {cheveronPlacement === 'right' && (
              <Icon
                className={clsx(
                  styles['accordion__title-icon-right'],
                  styles[`accordion__title-icon--margin-${cheveronMargin}`],
                  {
                    [styles['accordion__title-icon-right--expanded']]: isExpanded,
                  }
                )}
                name="CheveronLeft"
                size={16}
              />
            )}
          </div>
          <div
            aria-labelledby={accordionId}
            className={clsx(styles.accordion__content, { [styles['accordion__content--expanded']]: isExpanded })}
          >
            {content}
          </div>
        </div>
      </div>
    );
  }
);
